import type { AxiosResponse } from 'axios';
import { apiClient } from '@/shared/api';
import { handleRequest } from '@/shared/api/apiWrapper.ts';
import { objectToQueryParamsStr } from '@/shared/utils';
import type { Language } from '@/shared/types';
import type {
  BankDepositBanksResponse,
  BankDepositCountriesResponse,
  BankDepositCurrenciesResponse,
} from './types';

export type InvoiceFileType = 'pdf' | 'html';

const INVALID_RESPONSE_MESSAGE = 'Invalid response';

const assertNonEmptyArray = <T>(value: unknown): T[] => {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(INVALID_RESPONSE_MESSAGE);
  }

  return value as T[];
};

export const getBankDepositCountriesRequest =
  async (): Promise<BankDepositCountriesResponse> => {
    const request = () =>
      apiClient.get<BankDepositCountriesResponse>(
        '/operations/bank/deposit/countries'
      );
    const response = await handleRequest<BankDepositCountriesResponse>(request);

    return {
      countries: assertNonEmptyArray(response.data?.countries),
    };
  };

export const getBankDepositCurrenciesRequest = async (
  countryCode: string
): Promise<BankDepositCurrenciesResponse> => {
  const request = () =>
    apiClient.get<BankDepositCurrenciesResponse>(
      `/operations/bank/deposit/currencies?${objectToQueryParamsStr({ countryCode })}`
    );
  const response = await handleRequest<BankDepositCurrenciesResponse>(request);

  return {
    currencies: assertNonEmptyArray(response.data?.currencies),
  };
};

export const getBankDepositBanksRequest = async (
  countryCode: string,
  currencyCode: string
): Promise<BankDepositBanksResponse> => {
  const request = () =>
    apiClient.get<BankDepositBanksResponse>(
      `/operations/bank/deposit/banks?${objectToQueryParamsStr({ countryCode, currencyCode })}`
    );
  const response = await handleRequest<BankDepositBanksResponse>(request);

  return {
    banks: assertNonEmptyArray(response.data?.banks),
  };
};

export const getInvoiceFileRequest = async (
  invoiceId: string,
  fileType: InvoiceFileType,
  lang?: Language
): Promise<AxiosResponse<Blob | string>> => {
  const accept = fileType === 'pdf' ? 'application/pdf' : 'text/html';

  const urlParams = new URLSearchParams();

  if (fileType === 'pdf') urlParams.set('responseType', 'file');
  if (lang) urlParams.set('lang', lang);

  const query = urlParams.toString();

  const request = () =>
    apiClient.get(
      `/operations/bank/deposit/invoices/${invoiceId}/${fileType}${query ? `?${query}` : ''}`,
      {
        headers: { Accept: accept },
        responseType: fileType === 'pdf' ? 'blob' : undefined,
      }
    );

  return await handleRequest<Blob | string>(request);
};
